import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment-timezone';
import axios from 'axios';

import { TextField, Select, FormControl, InputLabel } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';

import DayPicker, { DateUtils, DayModifiers } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import { Loading } from '../stories/Loading';
import PrimaryButton from './lib/buttons/PrimaryButton';
import SecondaryButton from './lib/buttons/SecondaryButton';
import { FeatureContext } from '../App';

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '0%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  dayPickerContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
      alignSelf: 'center',
    },
    marginTop: '18px',
    borderRadius: '4px',
    marginBottom: '12px',
    border: 'solid 1px #004878',
    overflow: 'scroll',
    '& > div > div > .DayPicker-Months': {
      justifyContent: 'start',
    },
    '& .DayPicker-Day': {
      borderRadius: '0%',
    },
  },
  button: {
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    alignSelf: 'center',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 160,
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: 320,
    },
  },
  footerButtons: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down('md')]: {
      justifyContent: 'space-between',
    },
    [theme.breakpoints.up('md')]: {
      justifyContent: 'space-evenly',
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
      marginRight: '40px',
    },
  },
}));

const useCellStyles = makeStyles((theme) => ({
  cellRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'solid 1px #004878',
    [theme.breakpoints.down('sm')]: {
      width: 36,
      minHeight: 36,
    },
    [theme.breakpoints.up('sm')]: {
      minHeight: 60,
      width: 60,
    },
  },
  dateStyle: {
    color: 'black',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
    },
  },
  disabledDateStyle: {
    color: '#BEBEBE',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
    },
  },
  extraBedStyle: {
    position: 'relative',
    color: '#70360b',
    fontSize: '1em',
    [theme.breakpoints.down('sm')]: {
      bottom: -8,
      right: -4,
    },
    [theme.breakpoints.up('sm')]: {
      bottom: -16,
      right: -8,
    },
  },
}));

interface ExtraBedProps {}

const ExtraBed = (props: ExtraBedProps) => {
  const classes = useStyles();
  const cellStyles = useCellStyles();
  const features = useContext(FeatureContext);

  const [loading, setLoading] = useState(false);

  const [checkedRooms, setCheckedRooms] = useState(new Array<any>());
  const [extraBedsInfo, setExtraBedsInfo] = useState(new Map<string, any>());

  const [selectedRoom, setSelectedRoom] = useState<any>();
  const [selectedDates, setSelectedDates] = useState(new Array<Date>());
  const [extraBedsNo, setExtraBedsNo] = useState<string>('');

  useEffect(() => getAllCheckedRooms(), []);
  useEffect(() => fetchExtraBedInfo(selectedRoom?.checkin_info_id), [
    selectedRoom,
  ]);

  const getAllCheckedRooms = () => {
    setLoading(true);

    axios
      .get('/api/getAllCheckedRooms')
      .then(({ data }) => {
        setCheckedRooms(data.checkedRooms || []);
      })
      .catch((e) => {
        alert('Error fetching data');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchExtraBedInfo = (checkinInfoId: number) => {
    if (checkinInfoId > 0) {
      setLoading(true);

      axios
        .get(`/api/extraBedsInfo?checkin_info_id=${checkinInfoId}`)
        .then(({ data }) => data.results)
        .then((data) => {
          let extraBedsMap = new Map<string, any>();
          if (data && data.length > 0) {
            extraBedsMap = data.reduce((obj: Map<string, any>, item: any) => {
              obj.set(
                moment(item.requested_date).local().format('YYYY-MM-DD'),
                {
                  noOfBeds: item.no_of_beds,
                  requestId: item.extra_bed_request_id,
                }
              );
              return obj;
            }, new Map());
          }
          // console.log(extraBedsMap);
          setExtraBedsInfo(extraBedsMap);
        })
        .catch((e) => {
          alert('Failed to fetch extra bed info. Try again');
          console.error('Fetch Error: ', JSON.stringify(e));
        })
        .finally(() => setLoading(false));
    }
  };

  const cancelExtraBed = () => {
    const cancelRequestIds: any[] = [];

    if (selectedRoom === undefined) {
      alert('Select a room');
    } else if (selectedDates.length === 0) {
      alert('Select dates to remove extra bed');
    } else if (
      !selectedDates.every((day) => {
        let dateKey = day.toISOString().substring(0, 10);
        return extraBedsInfo.has(dateKey);
      })
    ) {
      alert('Not every room has extra bed.');
    } else {
      setLoading(true);
      const todayDateStr = new Date().toISOString().substring(0, 10);
      const todayTime = new Date(todayDateStr).getTime();

      selectedDates.forEach((day) => {
        if (day.getTime() >= todayTime) {
          let dateKey = day.toISOString().substring(0, 10);
          cancelRequestIds.push(
            extraBedsInfo.get(dateKey) && extraBedsInfo.get(dateKey).requestId
          );
        } else {
          alert('Cannot cancel extra beds for past days!');
        }
      });

      axios
        .post('/api/cancelExtraBedRequest', { cancelRequestIds })
        .then((res) => res.data)
        .then((res) => {
          if (res.errno > 0) {
            alert('Some internal error occurred.');
          } else {
            setSelectedDates([]);
            fetchExtraBedInfo(selectedRoom.checkin_info_id);
          }
        })
        .catch((e) => {
          alert('Some request exception occurred.');
          console.error('Error: ' + JSON.stringify(e));
        })
        .finally(() => setLoading(false));
    }
  };

  const handleSubmit = () => {
    if (selectedRoom === undefined) {
      alert('Select a room');
    } else if (selectedDates.length === 0) {
      alert('Select dates to add extra bed');
    } else if (extraBedsNo === '') {
      alert('Select number of extra beds');
    } else {
      let checkin_info_id = selectedRoom.checkin_info_id;
      let selected_dates = selectedDates.map((day) =>
        day.toISOString().substring(0, 10)
      );
      let no_of_beds = extraBedsNo;

      setLoading(true);

      axios
        .post('/api/registerExtraBeds', {
          checkin_info_id,
          selected_dates,
          no_of_beds,
        })
        .then((res) => res.data)
        .then((res) => {
          // console.log(res);
          if (res.errno > 0) {
            alert(res.message || 'Some error occured. Try again');
          } else {
            setSelectedDates([]);
            setExtraBedsNo('');
            fetchExtraBedInfo(selectedRoom.checkin_info_id);
          }
        })
        .catch((e) => {
          alert('Some request exception occurred.');
          console.error('Error: ' + JSON.stringify(e));
        })
        .finally(() => setLoading(false));
    }
  };

  const renderDay = (date: Date, { selected, disabled }: DayModifiers) => {
    const dateKey = date.toISOString().substring(0, 10);

    if (disabled) {
      return (
        <div className={cellStyles.cellRoot}>
          <div className={cellStyles.disabledDateStyle}>{date.getDate()}</div>
        </div>
      );
    } else {
      return (
        <div className={cellStyles.cellRoot}>
          <div className={cellStyles.dateStyle}>{date.getDate()}</div>
          {extraBedsInfo.get(dateKey) && (
            <div key={date.toISOString()} className={cellStyles.extraBedStyle}>
              {extraBedsInfo.get(dateKey).noOfBeds}
            </div>
          )}
        </div>
      );
    }
  };

  const handleDayClick = (day: Date, { selected, disabled }: DayModifiers) => {
    if (disabled) {
      return;
    }

    const selectedDays = selectedDates.slice();
    if (!selected && selectedDates.length === 5) {
      alert('Maxium of 5 days can be selected at once.');
    } else {
      if (selected) {
        const selectedIndex = selectedDays.findIndex((selectedDay) =>
          DateUtils.isSameDay(selectedDay, day)
        );
        selectedDays.splice(selectedIndex, 1);
      } else {
        selectedDays.push(day);
      }

      setSelectedDates(selectedDays);
    }
  };

  let limitDate = new Date();
  limitDate.setDate(limitDate.getDate() + 5);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Extra Bed Service</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <Autocomplete
              options={checkedRooms.sort((a, b) => a.room_name - b.room_name)}
              groupBy={(r: any) =>
                features.groupRoomsByBlockFeatureEnabled
                  ? r.block_name
                  : r.floor_str
              }
              getOptionLabel={(r: any) => r.room_name}
              style={{ width: 300, marginTop: '24px', marginBottom: '24px' }}
              renderInput={(params: { [x: string]: any }) => (
                <TextField {...params} label="Room No" variant="outlined" />
              )}
              onChange={(_, r) => {
                if (r != null) {
                  setSelectedRoom(r);
                } else {
                  setSelectedRoom(undefined);
                }
              }}
              value={selectedRoom || null}
            />
          </div>
          <hr className={classes.formSeparator} />
          <div className={classes.formContainer}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="outlined-bed-input">Beds</InputLabel>
              <Select
                style={{ width: 300 }}
                native
                value={extraBedsNo}
                onChange={(e) => setExtraBedsNo(e.target.value as string)}
                label="Beds"
                inputProps={{
                  name: 'beds',
                  id: 'outlined-bed-input',
                }}
              >
                <option aria-label="None" value="" />
                <option value={1}>1 Bed</option>
                <option value={2}>2 Beds</option>
                <option value={3}>3 Beds</option>
              </Select>
            </FormControl>
          </div>
        </div>
        <hr className={classes.separatorLine} />
        <div className={classes.dayPickerContainer}>
          <DayPicker
            disabledDays={[
              {
                before: selectedRoom
                  ? new Date(selectedRoom.checkin_date)
                  : new Date(),
                after: selectedRoom ? limitDate : new Date(),
              },
            ]}
            renderDay={renderDay}
            selectedDays={selectedDates}
            onDayClick={handleDayClick}
          />
        </div>
        <div className={classes.footerButtons}>
          <SecondaryButton onClick={cancelExtraBed} className={classes.button}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSubmit} className={classes.button}>
            Save
          </PrimaryButton>
        </div>
      </div>
    );
  }
};

export default ExtraBed;
